import React from 'react'
import Modal from 'react-modal'
import InputFormikField from '../forms/input-formik-field'
import SelectFormikField from '../forms/select-formik-field'
import TextareaFormikField from '../forms/textarea-formik-field'
import noScroll from 'no-scroll'
import { Formik, Form } from 'formik'
import { IStatisticsData, IStatisticsArrayData } from './statistics.interface'
import { emptyStatisticsData, validationSchema } from './statistics-helper'

interface EditProps {
  displayForm: boolean
  setDisplayForm: (displayForm: boolean) => void
  received: {
    data: IStatisticsData[]
    imported: boolean
  }
  selectedIndex: number
  latestIndex: number
  setReceived: (received: { data: IStatisticsData[]; imported: boolean }) => void
  setSelectedIndex: (index: number) => void
  setOutsideValues: (values: IStatisticsData) => void
  scb: boolean
  currentData: IStatisticsArrayData
}

const EditModal: React.FC<EditProps> = ({
  displayForm,
  setDisplayForm,
  received,
  selectedIndex,
  latestIndex,
  setSelectedIndex,
  setOutsideValues,
  scb,
  currentData,
}: EditProps) => {
  return (
    <Modal
      isOpen={displayForm}
      contentLabel="new ? Skapa ny post : Redigera post"
      style={{
        content: {
          maxWidth: '640px',
          marginLeft: 'auto',
          marginRight: 'auto',
          padding: 0,
        },
      }}
      onAfterOpen={() => noScroll.on()}
      onAfterClose={() => noScroll.off()}
      ariaHideApp={false}
    >
      <Formik
        enableReinitialize
        initialValues={
          received && received.data
            ? received.data.find((e) => e.index === selectedIndex)
              ? received.data.find((e) => e.index === selectedIndex)
              : emptyStatisticsData
            : emptyStatisticsData
        }
        validationSchema={validationSchema(received, scb)}
      >
        {({
          values,
          setFieldValue,
          handleReset,
          validateForm,
          setFieldTouched,
          errors,
          touched,
        }) => {
          setOutsideValues(values)

          if (values.error) {
            setFieldValue('error', '', false)
            validateForm(values).then((validationResult) => {
              for (const validationResultField in validationResult) {
                setFieldTouched(validationResultField, true, true)
              }
            })
          }

          return (
            <Form translate="" className="flex flex-col h-full">
              <div className="overflow-none flex-grow p-8">
                <h2 className="text-center pb-8 border-bottom">Kontrollera post</h2>
                {/* <Persist name='statistics-form' /> */}
                <InputFormikField
                  touched={touched}
                  errors={errors}
                  type="text"
                  label="Personnummer"
                  placeholder="ÅÅÅÅMMDDNNNN"
                  name="personalnumber"
                  required
                  readOnly="true"
                />
                <InputFormikField
                  touched={touched}
                  errors={errors}
                  type="text"
                  label="För- och efternamn"
                  helpText="Frivillig uppgift"
                  placeholder=""
                  name="name"
                  readOnly="true"
                />
                <SelectFormikField
                  touched={touched}
                  errors={errors}
                  label="Avtal"
                  placeholder="- Välj avtal -"
                  data={currentData.agreement}
                  name="agreement"
                  required
                  readOnly="true"
                />
                <InputFormikField
                  touched={touched}
                  errors={errors}
                  type="text"
                  label="Klassigo-kod"
                  helpText="Alla arbetstagare i rapporteringen ska klassificeras enligt Klassigo."
                  name="klassigoCode"
                  required
                  readOnly="true"
                />
                {values.klassigoCode &&
                (values.klassigoCode.toString() === '2310' ||
                  values.klassigoCode.toString() === '2319') && (
                  <SelectFormikField
                    touched={touched}
                    errors={errors}
                    name="klassigoSpecial"
                    placeholder="- Välj -"
                    helpText="Ska endast fyllas i för arbetstagare med Klassigo-kod 2310 / 2319 "
                    label="Klassigo-special"
                    data={currentData.klassigoSpecial}
                    required
                    readOnly="true"
                  />
                )}
                <InputFormikField
                  touched={touched}
                  errors={errors}
                  type="text"
                  label="Yrkesbenämning"
                  name="occupationalDesignation"
                  readOnly="true"
                  required
                />

                {scb && (
                  <span>
                    <SelectFormikField
                      touched={touched}
                      errors={errors}
                      type="text"
                      label="Anställningsform"
                      placeholder="- Välj anställningsform -"
                      data={currentData.employmentForm}
                      name="employmentForm"
                      required
                      readOnly="true"
                    />
                    <SelectFormikField
                      touched={touched}
                      errors={errors}
                      type="text"
                      label="Löneform"
                      placeholder="- Välj löneform -"
                      data={currentData.salaryForm}
                      name="salaryForm"
                      required
                      readOnly="true"
                    />
                  </span>
                )}
                <InputFormikField
                  touched={touched}
                  errors={errors}
                  type="number"
                  label={`${values.salaryForm === '3' && scb ? 'Timlön' : 'Fast månadslön'}`}
                  newLineForHelpText
                  name="fixedSalary"
                  step="0.01"
                  min="0"
                  lang="sv_SE"
                  required
                  readOnly="true"
                />
                <InputFormikField
                  touched={touched}
                  errors={errors}
                  type="number"
                  label="Tillägg för arbetsuppgifter"
                  name="extraSalary"
                  step="0.01"
                  min="0"
                  lang="sv_SE"
                  readOnly="true"
                />
                <InputFormikField
                  touched={touched}
                  errors={errors}
                  type="number"
                  label="Förmånsvärde"
                  name="benefits"
                  step="0.01"
                  min="0"
                  lang="sv_SE"
                  readOnly="true"
                />
                <InputFormikField
                  touched={touched}
                  errors={errors}
                  type="number"
                  label="Resultatlön"
                  newLineForHelpText
                  name="resultSalary"
                  step="0.01"
                  min="0"
                  lang="sv_SE"
                  readOnly="true"
                />
                <InputFormikField
                  touched={touched}
                  errors={errors}
                  type="number"
                  label="Veckoarbetstid"
                  newLineForHelpText
                  name="weeklyHours"
                  step="0.01"
                  min="0"
                  lang="sv_SE"
                  required
                  helpText={scb ? 'Genomsnittlig ordinarie veckoarbetstid per helgfri vecka' : ''}
                  readOnly="true"
                />
                {!scb && (
                    <InputFormikField
                      touched={touched}
                      errors={errors}
                      type="number"
                      label="Sysselsättningsgrad"
                      newLineForHelpText
                      name="partTimePercentage"
                      step="0.01"
                      min="0"
                      max="100"
                      lang="sv_SE"
                      required
                      readOnly="true"
                    />
                )}
                
                { scb && (values.salaryForm === 1 || values.salaryForm === 2) && (
                      <InputFormikField
                      touched={touched}
                      errors={errors}
                      type="number"
                      label="Sysselsättningsgrad"
                      newLineForHelpText
                      name="partTimePercentage"
                      step="0.01"
                      min="0"
                      max="100"
                      lang="sv_SE"
                      required
                      readOnly="true"
                    />
                )}

                {scb && (
                  <span>
                    <InputFormikField
                      touched={touched}
                      errors={errors}
                      type="number"
                      label="Arbetad tid"
                      newLineForHelpText
                      helpText="Faktiskt antal arbetade timmar under september månad"
                      name="workedHours"
                      step="0.01"
                      min="0"
                      lang="sv_SE"
                      required
                      readOnly="true"
                    />
                    <InputFormikField
                      touched={touched}
                      errors={errors}
                      type="number"
                      label="Mer- och övertidstimmar"
                      newLineForHelpText
                      name="overTimeHours"
                      step="0.01"
                      min="0"
                      lang="sv_SE"
                      readOnly="true"
                    />
                    {values.overTimeHours && (
                      <InputFormikField
                        touched={touched}
                        errors={errors}
                        type="number"
                        newLineForHelpText
                        label="Mer- och övertidsersättning"
                        name="overTimeSalary"
                        step="0.01"
                        min="0"
                        lang="sv_SE"
                        readOnly="true"
                      />
                    )}
                    <InputFormikField
                      touched={touched}
                      errors={errors}
                      type="number"
                      newLineForHelpText
                      label="Beredskapsersättning"
                      name="emergencyCompensation"
                      step="0.01"
                      min="0"
                      lang="sv_SE"
                      readOnly="true"
                    />
                  </span>
                )}
                <SelectFormikField
                  touched={touched}
                  errors={errors}
                  label="Arbetslivskonto - valt alternativ"
                  placeholder="- Välj -"
                  newLineForHelpText
                  data={currentData.workAccount}
                  name="workAccount"
                  readOnly="true"
                />
                <InputFormikField
                  touched={touched}
                  errors={errors}
                  type="number"
                  label="Arbetslivskonto - pensionspremie"
                  newLineForHelpText
                  name="workAccountValue"
                  step="0.01"
                  min="0"
                  lang="sv_SE"
                  readOnly="true"
                />
                <InputFormikField
                  touched={touched}
                  errors={errors}
                  type="number"
                  label="Arbetslivskonto - timmar"
                  newLineForHelpText
                  name="shorterWorkingHours"
                  step="0.01"
                  min="0"
                  lang="sv_SE"
                  readOnly="true"
                />
                <InputFormikField
                  touched={touched}
                  errors={errors}
                  type="number"
                  label="Semesterdagar"
                  newLineForHelpText
                  name="vacationDays"
                  min="25"
                  step="0.01"
                  lang="sv_SE"
                  required
                  readOnly="true"
                />
                {scb && (
                  <InputFormikField
                    touched={touched}
                    errors={errors}
                    type="number"
                    label="Övriga tillägg"
                    name="otherAdditions"
                    step="0.01"
                    min="0"
                    lang="sv_SE"
                    readOnly="true"
                  />
                )}
                <TextareaFormikField
                  touched={touched}
                  errors={errors}
                  label="Kommentar"
                  newLineForHelpText
                  name="comment"
                  readOnly="true"
                />
              </div>
              <div className="flex justify-center mt-6 pb-8 px-10">
                <div>
                  <button
                    type="button"
                    className="btn-white"
                    onClick={() => {
                      handleReset()
                      setSelectedIndex(latestIndex)
                      const firstElement = document.querySelector('[name="personalnumber"]')
                      if (firstElement) {
                        window.scrollBy({
                          top: firstElement.getBoundingClientRect().top - 55,
                          left: 0,
                          behavior: 'smooth',
                        })
                        setTimeout(() => {
                          firstElement.focus()
                        }, 500)
                      }
                      setDisplayForm(false)
                    }}
                  >
                    <span>Stäng fönster</span>
                  </button>
                </div>
              </div>
            </Form>
          )
        }}
      </Formik>
    </Modal>
  )
}

export default EditModal
